import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
if (!API_URL) {
  console.error('REACT_APP_BACKEND_URL environment variable is not set!');
}

// Token synchron setzen, damit die ersten Requests nach einem Reload sofort authentifiziert sind
// (verhindert „Race"-401s bevor der React-Effekt den Header setzt → spurious Logout).
try {
  const _savedToken = typeof localStorage !== 'undefined' && localStorage.getItem('token');
  if (_savedToken) axios.defaults.headers.common['Authorization'] = `Bearer ${_savedToken}`;
} catch (e) {}

async function _tryRefresh() {
  try {
    const tok = localStorage.getItem('token');
    if (!tok) return null;
    const r = await axios.post(`${API_URL}/api/auth/refresh`, {}, { headers: { Authorization: `Bearer ${tok}` }, _skipAuthRetry: true });
    const nt = r.data && r.data.access_token;
    if (nt) {
      localStorage.setItem('token', nt);
      axios.defaults.headers.common['Authorization'] = `Bearer ${nt}`;
      return nt;
    }
  } catch (e) {}
  return null;
}

function _forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    try { sessionStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search + window.location.hash); } catch (e) {}
    window.location.href = '/login';
  }
}

// Globaler 401-Handler: Bei 401 zuerst EINMAL das Token erneuern und den Request wiederholen.
// Nur wenn die Erneuerung scheitert (Token wirklich ungültig), wird abgemeldet.
// Login/Registrierung sind ausgenommen (dort ist 401 = falsche Zugangsdaten).
axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error && error.response && error.response.status;
    const cfg = (error && error.config) || {};
    const url = cfg.url || '';
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/register');
    const isRefresh = url.includes('/api/auth/refresh');
    const hasToken = !!localStorage.getItem('token');
    if (status === 401 && hasToken && !isAuthEndpoint && !isRefresh && !cfg._skipAuthRetry && !cfg._authRetried) {
      const nt = await _tryRefresh();
      if (nt) {
        cfg._authRetried = true;
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${nt}` };
        return axios(cfg);
      }
      _forceLogout();
    } else if (status === 401 && hasToken && (isRefresh || cfg._authRetried)) {
      _forceLogout();
    }
    return Promise.reject(error);
  }
);

// Helper: Asset-URL – akzeptiert absolute URLs (CDN) und DB-File-IDs
function assetUrl(v) {
  if (!v) return '';
  return String(v).startsWith('http') ? v : `${API_URL}/api/files/${v}`;
}

// Helper: YouTube ID extrahieren
function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,
    /(?:youtube\.com\/live\/)([^&\n?#]+)/,
    /(?:youtu\.be\/)([^&\n?#]+)/,
    /(?:youtube\.com\/embed\/)([^&\n?#]+)/,
    /(?:youtube\.com\/v\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (let pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return url; // Fallback: use URL as-is (might be just the ID)
}

// Helper: Event-Datumsbereich formatieren
function formatEventDateRange(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Gleicher Tag
  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  }
  
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString('de-DE', { month: 'long' });
  const endMonth = end.toLocaleDateString('de-DE', { month: 'long' });
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  
  // Gleiches Jahr und Monat
  if (startYear === endYear && startMonth === endMonth) {
    return `${startDay}. – ${endDay}. ${startMonth} ${startYear}`;
  }
  
  // Gleiches Jahr, verschiedene Monate
  if (startYear === endYear) {
    return `${startDay}. ${startMonth} – ${endDay}. ${endMonth} ${startYear}`;
  }
  
  // Verschiedene Jahre
  return `${startDay}. ${startMonth} ${startYear} – ${endDay}. ${endMonth} ${endYear}`;
}

// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  // Initialize user from localStorage to prevent redirect on refresh
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(!!localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      // Gleitende Erneuerung: Token bei jedem App-Start verlängern (ohne Re-Render-Schleife)
      try {
        const rr = await axios.post(`${API_URL}/api/auth/refresh`);
        if (rr.data && rr.data.access_token) {
          localStorage.setItem('token', rr.data.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${rr.data.access_token}`;
        }
      } catch (e) {}
    } catch (error) {
      // Nur bei echtem Auth-Fehler (401/403) abmelden. Vorübergehende Fehler
      // (Netzwerk, 5xx) dürfen NICHT die Sitzung beenden.
      const status = error.response && error.response.status;
      if (status === 401 || status === 403) {
        logout();
      } else {
        const cached = localStorage.getItem('user');
        if (cached) { try { setUser(JSON.parse(cached)); } catch (e) {} }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await axios.post(`${API_URL}/api/auth/login`, formData);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(access_token);
    setUser(newUser);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  };

  const clearSavedNav = () => {
    sessionStorage.removeItem('adminActiveTab');
    sessionStorage.removeItem('dashboardSection');
    localStorage.removeItem('adminActiveTab');
    localStorage.removeItem('dashboardSection');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearSavedNav();
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/';
  };

  // Inaktivitaets-Timer / automatischer Logout DEAKTIVIERT.
  // Angemeldete Personen bleiben angemeldet (nur manuelles Abmelden oder ungültiges Token beendet die Sitzung).
  const NORMAL_TIMEOUT = 15 * 60;
  const [extendedTimeout, setExtendedTimeout] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(NORMAL_TIMEOUT);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, inactivityCountdown, loading, setExtendedTimeout }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return React.useContext(AuthContext);
}

// Header Component

// Gestückelter Upload (umgeht Proxy-Limits) mit echtem Fortschritt + Hintergrund-Komprimierung.
// onProgress(percent, phase) — phase: 'uploading' | 'processing'
async function uploadFileChunked(file, { imageType = 'event_flyer', purpose = 'cms', onProgress } = {}) {
  const token = localStorage.getItem('token');
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const CHUNK = 4 * 1024 * 1024; // 4 MB
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));

  for (let i = 0; i < totalChunks; i++) {
    const chunk = file.slice(i * CHUNK, (i + 1) * CHUNK);
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', i);
    fd.append('total_chunks', totalChunks);
    fd.append('file', chunk, file.name);
    await axios.post(`${API_URL}/api/upload/chunk`, fd, {
      headers: authHeader,
      onUploadProgress: (e) => {
        if (!onProgress) return;
        const loaded = i * CHUNK + (e.loaded || 0);
        const pct = Math.min(99, Math.round((loaded / file.size) * 100));
        onProgress(pct, 'uploading');
      },
    });
    if (onProgress) {
      const pct = Math.min(99, Math.round((((i + 1) * CHUNK) / file.size) * 100));
      onProgress(pct, 'uploading');
    }
  }

  const ff = new FormData();
  ff.append('upload_id', uploadId);
  ff.append('filename', file.name);
  ff.append('content_type', file.type || '');
  ff.append('image_type', imageType);
  ff.append('purpose', purpose);
  const fin = await axios.post(`${API_URL}/api/upload/finalize`, ff, { headers: authHeader });
  let job = fin.data;

  if (onProgress) onProgress(100, 'processing');
  while (job.status === 'processing') {
    await new Promise((r) => setTimeout(r, 1500));
    const st = await axios.get(`${API_URL}/api/upload/status/${job.job_id}`, { headers: authHeader });
    job = st.data;
  }
  if (job.status === 'error') throw new Error(job.error || 'Upload fehlgeschlagen');
  return job.result; // {file_id, filename, content_type, ...}
}

// TTS-Vorbild: MP3 per Auth-Header als Blob laden (kein Token in der URL), cachen und abspielen.
const _ttsCache = new Map();
let _ttsCurrent = null;
async function playTTS(text, voice = 'onyx', speed = 1.0) {
  const t = (text || '').trim();
  if (!t) return false;
  const key = `${voice}|${speed}|${t}`;
  let url = _ttsCache.get(key);
  if (!url) {
    const r = await axios.get(`${API_URL}/api/language/tts`, { params: { text: t, voice, speed }, responseType: 'blob', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    url = URL.createObjectURL(r.data);
    _ttsCache.set(key, url);
  }
  if (_ttsCurrent) { try { _ttsCurrent.pause(); } catch (e) {} }
  const a = new Audio(url);
  _ttsCurrent = a;
  await a.play();
  return new Promise(resolve => { a.onended = () => resolve(true); a.onerror = () => resolve(false); });
}

// TTS-Einstellungen (Stimme + Tempo) aus localStorage
const TTS_VOICES = [
  { id: 'onyx', label: 'Onyx (männlich)' },
  { id: 'echo', label: 'Echo (männlich)' },
  { id: 'nova', label: 'Nova (weiblich)' },
  { id: 'shimmer', label: 'Shimmer (weich)' },
];
function ttsPrefs() {
  return { voice: localStorage.getItem('tts_voice') || 'onyx', speed: parseFloat(localStorage.getItem('tts_speed') || '1') || 1 };
}
// Artikel-Felder (Entitäten/Bücher) robust in [{heading,text}] normalisieren – manche Datensätze speichern article als HTML-String
function normalizeArticle(a) {
  if (Array.isArray(a)) return a;
  if (typeof a === 'string' && a.trim()) {
    const txt = a.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    return txt ? [{ heading: '', text: txt }] : [];
  }
  return [];
}
export { API_URL, assetUrl, extractYouTubeId, formatEventDateRange, AuthContext, AuthProvider, useAuth, uploadFileChunked, playTTS, ttsPrefs, TTS_VOICES, normalizeArticle };
